import {AppBar, Box, Button, IconButton, LinearProgress, Stack, Toolbar, Typography, useTheme} from "@mui/material";
import * as React from 'react';
import {useEffect, useState} from 'react';
import Logo from '../../images/HeaderLogoPro.png';
import ChatbotImg from '../../images/Chatbot.png';
import ChatbotOpenImg from '../../images/ChatbotOpen.png'
import MarginRow from "./MarginRow";
import SimpleMenu from "../navigation/SimpleMenu";
import config from 'react-global-configuration';
import {useResources} from "../../stores/ResourceProvider";
import {CreateUserSettingInput, UpdateUserSettingInput, UserType} from "../../API";
import {globalColors} from "../../styles/globalStyles";
import SubscriptionEditDialog from "../../pages/settings/SubscriptionEditDialog";
import User from "../../model/User";
import RoutesConfig from "../../RoutesConfig";
import {useNavigate} from "react-router-dom";
import useNavigationHelper from "../NavigationHelper";
import Tracking from "../Tracking";
import SocialLinks from "./SocialLinks";
import {UserSettingType} from "../../model/UserSetting";
import FreeTrialDialog from "../FreeTrialDialog";
import {getISODateToday} from "../../stores/StoreUtilities";
import useIsMobile from "../useIsMobile";

interface IHeaderProps {
  chatOpen?: boolean
  chatView?: string
  onChange?(chat: boolean): void
}

const Header = ({
  chatOpen,
  chatView,
  onChange
} : IHeaderProps) => {
  const isMobile = useIsMobile()
  const [user, setUser] = useState<User | undefined>()
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isFree, setIsFree] = useState<boolean>(false)
  const [isTrial, setIsTrial] = useState<boolean>(false)
  const [isClient, setIsClient] = useState<boolean>(false)
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | undefined>()
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false)
  const [showFreeTrialDialog, setShowFreeTrialDialog] = useState<boolean>(false)

  const { progress, userStore} = useResources()
  const navigate = useNavigate()
  const {navigateInvest} = useNavigationHelper()

  const theme = useTheme()
  const styles = {
    premiumButton: {
      color: globalColors.primary,
      backgroundColor: globalColors.webFlowButton,
      letterSpacing: "1px",
      textTransform: "capitalize",
      borderRadius: "50px",
      marginLeft: "20px",
      paddingLeft: "8px",
      paddingRight: "8px",
      paddingTop: 0, // "12px",
      paddingBottom: 0, // "12px",
      fontSize: "14px !important",
      fontWeight: 500,
      lineHeight: "20px",
      "&:hover": {
        color: globalColors.webFlowButton,
        backgroundColor: globalColors.primary
      }
    },
    discoverButton: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      letterSpacing: "1px",
      textTransform: "capitalize",
      borderRadius: "50px",
      marginLeft: "20px",
      paddingLeft: "8px",
      paddingRight: "8px",
      paddingTop: 0, // "12px",
      paddingBottom: 0, // "12px",
      fontSize: "14px !important",
      fontWeight: 500,
      lineHeight: "20px",
      "&:hover": {
        color: theme.palette.primary.contrastText,
        backgroundColor: globalColors.webFlowButton
      }
    },
    investButton: {
      color: theme.palette.primary.main,
      backgroundColor: globalColors.webFlowButton,
      letterSpacing: "1px",
      textTransform: "capitalize",
      borderRadius: "50px",
      marginLeft: "20px",
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingTop: 0, // "12px",
      paddingBottom: 0, // "12px",
      fontSize: "14px !important",
      fontWeight: 500,
      lineHeight: "20px",
      "&:hover": {
        color: theme.palette.primary.contrastText,
        backgroundColor: globalColors.webFlowButton
      }
    }

  }

  useEffect(() => {
    const user = userStore.user
    setUser(user)
    setIsChatOpen(user !== undefined && chatOpen === true)
    setIsAuthenticated(user !== undefined)
    setIsFree(user !== undefined && user.userType === UserType.Free)
    setIsTrial(user !== undefined && user.userType === UserType.Trial)
    setIsClient(user !== undefined && user.userType === UserType.Client)
    if (user && (user.userType === UserType.Trial || user.userType === UserType.Free)) {
      const daysRemaining = userStore.getTrialDaysRemaining()
      setTrialDaysRemaining(daysRemaining)
      if (daysRemaining !== undefined && daysRemaining <= 1) {
        const freeTrialUserSetting = userStore.user?.getUserSetting(UserSettingType.freeTrial, "")
        if (!freeTrialUserSetting || !freeTrialUserSetting.settings["reminderDate"] ||
          (freeTrialUserSetting.settings["reminderDate"] < getISODateToday() && freeTrialUserSetting.settings["reminderDate"] <= user.trialEndDate)) {
          setShowFreeTrialDialog(true)
        }
      }
    }
  }, [chatOpen, userStore.user])

  const handleFreeTrialAction = async (action: string) => {
    setShowFreeTrialDialog(false)

    // Set freeTrial UserSetting reminderDate
    const userSetting = userStore.user?.getUserSetting(UserSettingType.freeTrial, "")
    if (userSetting) {
      const input: UpdateUserSettingInput = {
        id: userSetting.id,
        settings: JSON.stringify({
          reminderDate: getISODateToday()
        })
      }
      await userStore.updateUserSetting(input)
    } else {
      const input: CreateUserSettingInput = {
        userId: userStore.user!.id,
        ownerId: userStore.user!.id,
        accountId: userStore.user!.accountId,
        name: "",
        settingType: UserSettingType.freeTrial,
        settings: JSON.stringify({
          reminderDate: getISODateToday()
        })
      }
      await userStore.createUserSetting(input)
    }

    if (action === "subscribe") {
      setShowSubscriptionDialog(true)
    }
  }

  const homeUrl = config.get("homeUrl")
  const disableSignup = config.get("disableSignup")

  return (
      <AppBar 
        position="fixed" 
        sx={{
          top: 0,
          backgroundColor: theme.palette.background.default,
          height: 76,
          maxHeight: 76,
          zIndex: theme.zIndex.drawer + 1,
          boxShadow: "none",
          // borderBottomStyle: "solid",
          // borderBottomWidth: 1,
          // borderBottomColor: theme.palette.grey[400]
        }}
      >
        <MarginRow>
          <Toolbar disableGutters={true}
            sx={{
              minHeight: 76,
              height: 76,
              maxHeight: 76,
              width: "100%",
              alignItems: "flex-start",
              paddingTop: "4px"
            }}
          >
            <a href={homeUrl} style={{ marginBottom: '-5px', marginLeft: 5 }}>
              <img src={Logo} alt="logo" height={50}/>
            </a>
            <Typography variant="h6" color="inherit" sx={{ flexGrow: 1 }} />
            <Stack sx={{display:"flex", flexGrow:1, justifyContent:"flex-end", whiteSpace:"nowrap"}}>
              <Box display="flex" flexGrow={1} m={0} p={0} justifyContent="flex-end" whiteSpace="nowrap">
                <SimpleMenu />
                {!isFree &&
                  <IconButton sx={{marginRight:0, top:-4}}
                              onClick={(event: any) => {
                                if (onChange) {
                                  if (!isChatOpen) {
                                    Tracking.event({action: "Ask Finn Opened"})
                                  }
                                  onChange(!isChatOpen)
                                }
                              }}
                  >
                    {isAuthenticated && isChatOpen &&
                      <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius:"20px"}}/>
                    }
                    {isAuthenticated && !isChatOpen &&
                      <img src={ChatbotImg} alt="chatbot" height={36} style={{borderRadius:"20px"}}/>
                    }
                  </IconButton>
                }
              </Box>
              <Box sx={{display:"flex", flexGrow:1, justifyContent:"flex-end", minHeight: 20, height: 20,
                        paddingRight:isAuthenticated? 1 : 4, position:"relative", top:-4}}>
                {!isMobile && <SocialLinks/>}
                {isAuthenticated && !isClient &&
                  <Button type="button" sx={styles.discoverButton}
                          onClick={() => navigateInvest()}
                  >
                    Invest
                  </Button>
                }
                {isFree &&
                  <Button type="button" sx={styles.premiumButton}
                          onClick={() => {
                            Tracking.event({action: "Click Unlock RBC Premium"})
                            setShowSubscriptionDialog(true)}
                          }
                  >
                    Unlock RBC Premium
                  </Button>
                }
                {isTrial && trialDaysRemaining !== undefined && trialDaysRemaining >= 0 &&
                  <Button type="button" sx={styles.premiumButton}
                          onClick={() => {
                            Tracking.event({action: "Click Unlock RBC Premium"})
                            setShowSubscriptionDialog(true)}
                          }
                  >
                    {isMobile ?  `${trialDaysRemaining} Trial Day${trialDaysRemaining === 1 ? "" : "s"}` :
                                 `Upgrade Now - ${trialDaysRemaining} Day${trialDaysRemaining === 1 ? "" : "s"} Left in Trial`
                    }
                  </Button>
                }
                {!isAuthenticated && !disableSignup &&
                  <Button type="button" sx={styles.premiumButton}
                          onClick={() => navigate(RoutesConfig.signUp)}
                  >
                    Get Started
                  </Button>
                }
              </Box>
            </Stack>
          </Toolbar>
        </MarginRow>
        <Box 
          sx={{ 
            flexGrow: 1, 
            height: 3 
          }}
        >
          { progress!.isVisible && 
            <LinearProgress 
              sx={{
                height: 3,
                backgroundColor: "#d72222" // Logo red
              }}
            />
          }
        </Box>
        {showSubscriptionDialog && user &&
          <SubscriptionEditDialog user={user}
                                  open={showSubscriptionDialog}
                                  onSave={(update: User) => {
                                    setUser(update)
                                    setShowSubscriptionDialog(false)
                                  }}
                                  onClose={() => {
                                    setShowSubscriptionDialog(false)
                                  }}/>
        }
        {showFreeTrialDialog &&
          <FreeTrialDialog open={showFreeTrialDialog}
                           onAction={handleFreeTrialAction}
                           />
        }
      </AppBar>
    )
}

export default Header